import React, { useEffect, useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FIELDS = ["owner_name", "owner_photo", "owner_photo_alt", "about_heading", "about_story", "about_tagline"];

export default function AdminAbout() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef(null);

  useEffect(() => {
    api.get("/site-content").then((r) => {
      const f = {};
      FIELDS.forEach((k) => (f[k] = r.data[k] || ""));
      setForm(f);
    });
  }, []);

  const uploadPhoto = async (fileList) => {
    const file = (fileList || [])[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, owner_photo: r.data.url }));
      toast.success("Photo uploaded — remember to Save");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site-content", form);
      toast.success("About page saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-navy/50">Loading…</div>;

  return (
    <div data-testid="admin-about">
      <h1 className="font-display text-4xl text-navy">About Page / Meet the Owner</h1>
      <p className="text-ink/60 mt-1">Edit your owner photo and story. Changes appear on the public About page.</p>

      <div className="mt-6 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <label className="block text-sm font-bold text-navy uppercase">Owner photo</label>
          {form.owner_photo ? (
            <div className="mt-2 relative w-full max-w-xs">
              <img data-testid="about-owner-photo" src={form.owner_photo} alt="Owner preview" className="w-full rounded-xl object-cover aspect-[4/5] border-4 border-cream shadow" />
              <button type="button" data-testid="about-remove-photo" onClick={() => setForm({ ...form, owner_photo: "" })}
                className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="mt-2 w-full max-w-xs rounded-xl bg-navy/5 border-2 border-dashed border-navy/20 aspect-[4/5] flex items-center justify-center text-navy/40 text-sm">No photo yet</div>
          )}
          <input ref={photoRef} type="file" accept="image/*" className="hidden"
            data-testid="about-photo-input"
            onChange={(e) => { uploadPhoto(e.target.files); e.target.value = ""; }} />
          <button type="button" data-testid="about-upload-btn" onClick={() => photoRef.current?.click()} disabled={uploading}
            className="mt-3 inline-flex items-center gap-2 bg-navy text-white font-bold uppercase text-sm rounded-md px-4 py-2.5 hover:bg-brick transition-colors disabled:opacity-60">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload photo
          </button>

          <label className="block text-sm font-bold text-navy uppercase mt-5">Photo alt text (accessibility / SEO)</label>
          <input data-testid="about-alt" value={form.owner_photo_alt} onChange={(e) => setForm({ ...form, owner_photo_alt: e.target.value })}
            className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />
        </div>

        <div className="lg:col-span-2">
          <label className="block text-sm font-bold text-navy uppercase">Section heading</label>
          <input data-testid="about-heading" value={form.about_heading} onChange={(e) => setForm({ ...form, about_heading: e.target.value })}
            className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />

          <label className="block text-sm font-bold text-navy uppercase mt-4">Your story <span className="text-ink/40 normal-case font-normal">(separate paragraphs with a blank line)</span></label>
          <textarea data-testid="about-story" rows={14} value={form.about_story} onChange={(e) => setForm({ ...form, about_story: e.target.value })}
            className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick leading-relaxed" />

          <label className="block text-sm font-bold text-navy uppercase mt-4">Tagline</label>
          <input data-testid="about-tagline" value={form.about_tagline} onChange={(e) => setForm({ ...form, about_tagline: e.target.value })}
            className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />

          <button data-testid="about-save-btn" onClick={save} disabled={saving}
            className="mt-6 bg-brick text-white font-bold uppercase rounded-md px-8 py-3 hover:bg-navy transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save About Page"}
          </button>
        </div>
      </div>
    </div>
  );
}
